import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import client from 'apolloClient';
import { Avatar, Space, Tabs } from 'antd';
import { PATIENT_FRAGMENT } from 'gql/patients/fragments';
import { useParams, useSearchParams } from 'react-router-dom';
import { calculateAge } from '@medicusunion/front-utils';
import { useTranslation } from 'react-i18next';
import { GENDERS_VALUES } from 'constants/profile';
import {
  // BarChartOutlined,
  CalendarOutlined,
  ExperimentOutlined,
  FileDoneOutlined,
  FileImageOutlined,
  FileTextOutlined,
  ReconciliationOutlined,
  UserOutlined
} from '@ant-design/icons';
import { PatientFragmentFragment } from '__generated__/graphql';

import { StyledEmpty, StyledPatientInfo } from './styled';
import { getFileUrl } from 'utils/file';
import Appointments from '../Appointments';
import Files from '../Files';
import Reports from '../Reports';
import Folders from '../Folders';
// import HealthApp from '../HealthApp';
import TestList from '../TestList';
import { PatientHealthDetails } from 'components/shared';
import TestItem from '../TestItem';
// import HealthApp from '../HealthApp';

const PatientInfo: FC = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [appointmentsCount, setAppointmentsCount] = useState(0);
  const [reportsCount, setReportsCount] = useState(0);
  const [filesCount, setFilesCount] = useState(0);
  const [sharedFoldersCount, setSharedFoldersCount] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const resultId = searchParams.get('result_id');

  useEffect(
    () => () => {
      setAppointmentsCount(0);
      setReportsCount(0);
      setFilesCount(0);
      setSharedFoldersCount(0);
    },
    [id]
  );

  const patient: PatientFragmentFragment | null = client.readFragment({
    fragment: PATIENT_FRAGMENT,
    fragmentName: 'PatientFragment',
    id: `Patient:${id}`
  });

  const ageStr = patient?.date_of_birth
    ? t('patient.years', {
        num: calculateAge(patient.date_of_birth)
      })
    : '';

  const activeTab = useMemo(() => {
    const pageParam = searchParams.get('page');
    const page = pageParam?.split('/')?.[0]?.replace('/', '');

    return page;
  }, [searchParams]);

  const onChangeTab = useCallback(
    (tab: string) => {
      searchParams.set('page', tab);
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    if (patient && !activeTab) {
      onChangeTab('appointments');
    }
  }, [activeTab, patient, onChangeTab]);

  const showHealthDetails = Object.keys(patient?.health_info || {}).length;

  if (!patient) {
    return <StyledEmpty description={t('messages.no_data')} />;
  }

  if (resultId) {
    return <TestItem id={resultId} />;
  }

  return (
    <StyledPatientInfo>
      <div className="patient-info-header">
        <Space size={16}>
          <Avatar
            size={40}
            src={getFileUrl(patient.avatar, '', 150)}
            icon={<UserOutlined />}
            style={{ backgroundColor: '#2552f3' }}
          />
          <p>
            <b>
              {patient.first_name} {patient.last_name},
            </b>{' '}
            {ageStr}
            {patient.gender
              ? `, ${t(`patient.${GENDERS_VALUES[patient.gender]}`)}`
              : null}
          </p>
        </Space>
        <p>
          <b>ID: #{id}</b>
        </p>
      </div>
      <Tabs
        className="patient-info-tab"
        activeKey={activeTab}
        onChange={onChangeTab}
        items={[
          {
            forceRender: true,
            label: (
              <Space>
                <CalendarOutlined />
                {t('patient.appointments')}
                {appointmentsCount ? `(${appointmentsCount})` : ''}
              </Space>
            ),
            key: 'appointments',
            children: id ? (
              <Appointments id={id} setTotal={v => setAppointmentsCount(v)} />
            ) : null
          },
          {
            forceRender: true,
            label: (
              <Space>
                <FileImageOutlined />
                {t('patient.all_files')}
                {filesCount ? `(${filesCount})` : ''}
              </Space>
            ),
            key: 'all_files',
            children: id ? (
              <Files id={id} setTotal={v => setFilesCount(v)} />
            ) : null
          },
          {
            forceRender: true,
            label: (
              <Space>
                <FileDoneOutlined />
                {t('patient.all_reports')}
                {reportsCount ? `(${reportsCount})` : ''}
              </Space>
            ),
            key: 'all_reports',
            children: id ? (
              <Reports id={id} setTotal={v => setReportsCount(v)} />
            ) : null
          },
          {
            forceRender: true,
            label: (
              <Space>
                <FileTextOutlined />
                {t('patient.shared_folders')}
                {sharedFoldersCount ? `(${sharedFoldersCount})` : ''}
              </Space>
            ),
            key: 'shared_folders',
            children: id ? (
              <Folders id={id} setTotal={v => setSharedFoldersCount(v)} />
            ) : null
          },
          ...(showHealthDetails
            ? [
                {
                  forceRender: false,
                  label: (
                    <Space>
                      <ReconciliationOutlined />
                      {t('appointments_details.health_details')}
                    </Space>
                  ),
                  key: 'health_details',
                  children: <PatientHealthDetails data={patient} />
                }
              ]
            : []),
          ...(patient.id
            ? [
                {
                  forceRender: false,
                  label: (
                    <Space>
                      <ExperimentOutlined />
                      {t('patient.test_results')}
                    </Space>
                  ),
                  key: 'test_results',
                  children: <TestList patientId={patient.id} />
                }
              ]
            : [])
          // ...(patient.id
          //   ? [
          //       {
          //         forceRender: false,
          //         label: (
          //           <Space>
          //             <BarChartOutlined />
          //             {t('patient.health_app')}
          //           </Space>
          //         ),
          //         key: 'health_app',
          //         children: <HealthApp patientId={patient.id} />
          //       }
          //     ]
          //   : [])
        ]}
      />
    </StyledPatientInfo>
  );
};

export default PatientInfo;
